import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useState } from 'react';
import ggLogoBlack from '../../assets/gg-black.svg';
import ggLogoWhite from '../../assets/gg-white.svg';
import { landingTheme } from './theme';

interface NavItem {
  label: string;
  href: string;
}

const navItems: NavItem[] = [
  { label: 'Overview', href: '#home' },
  { label: 'Features', href: '#features' },
  { label: 'About', href: '#about' },
  { label: 'FAQs', href: '#faqs' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'News', href: '#news' },
  { label: 'Contact', href: '#contact' },
];

export default function NavBar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: 'sticky',
          top: 0,
          left: 0,
          width: '100%',
          backgroundColor: landingTheme.colors.primary,
          zIndex: 1100,
        }}
      >
        <Box
          sx={{
            maxWidth: '1536px',
            mx: 'auto',
            px: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2,
          }}
        >
          {/* Logo */}
          <Box
            component="a"
            href="#home"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
            }}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick('#home');
            }}
          >
            <Box
              component="img"
              src={ggLogoWhite}
              alt="GrantGuardian"
              sx={{ height: 40 }}
            />
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 3,
              alignItems: 'center',
            }}
          >
            {navItems.map((item) => (
              <Box
                key={item.label}
                component="button"
                onClick={() => handleNavClick(item.href)}
                sx={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'capitalize',
                  transition: 'color 0.3s ease',
                  fontFamily: 'inherit',
                  '&:hover': {
                    color: landingTheme.colors.gold,
                  },
                }}
              >
                {item.label}
              </Box>
            ))}
            <Button
              variant="contained"
              href="/login"
              sx={{
                ml: 2,
                borderRadius: '24px',
                px: 3,
                textTransform: 'capitalize',
                fontWeight: 600,
                color: landingTheme.colors.black,
                backgroundColor: landingTheme.colors.gold,
                '&:hover': {
                  backgroundColor: landingTheme.colors.accent,
                },
              }}
            >
              Sign In
            </Button>
          </Box>

          {/* Mobile Menu Icon */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              display: { md: 'none' },
              color: 'white',
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
        }}
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={ggLogoBlack}
            alt="GrantGuardian"
            sx={{ height: 35 }}
          />
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          {navItems.map((item) => (
            <ListItem
              key={item.label}
              onClick={() => handleNavClick(item.href)}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemText primary={item.label} />
            </ListItem>
          ))}
          <ListItem sx={{ mt: 2 }}>
            <Button
              variant="contained"
              fullWidth
              href="/login"
              sx={{
                borderRadius: '24px',
                color: landingTheme.colors.black,
                backgroundColor: landingTheme.colors.gold,
                '&:hover': {
                  backgroundColor: landingTheme.colors.accent,
                },
              }}
            >
              Sign In
            </Button>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
